import { toast } from "@/components/common/Toast"
import { useGetUsersLimits } from "@/services/api/userService"
import { UserLimits } from "@/types/userLimits"
import { useEffect, useState } from "react"
import useFreeTrial from "./useFreeTrial"
import { useTranslation } from "./useTranslation/useTranslation"

const EXPIRED_LIMIT = 0

const useUserLimits = () => {
  const { translate } = useTranslation()
  const [userLimits, setUserLimits] = useState<UserLimits | null>()
  const { isExpired } = useFreeTrial()
  const { data, isLoading, error } = useGetUsersLimits()

  useEffect(() => {
    if (!isLoading && data) {
      const { resourceUsage } = data
      setUserLimits({ resourceUsage })
    }
    if (error) {
      // Fixed id: several components read the limits at once (sidebar, pages)
      toast.error(translate("USER_LIMITS/LOAD_ERROR"), {
        id: "user-limits-load-error",
      })
    }
  }, [data, isLoading, error, translate])

  if (isExpired) {
    return {
      filesLimit: EXPIRED_LIMIT,
      questionnairesLimit: EXPIRED_LIMIT,
      filesUsage: EXPIRED_LIMIT,
      questionnairesUsage: EXPIRED_LIMIT,
      chatMessagesLimit: EXPIRED_LIMIT,
      chatMessagesUsage: EXPIRED_LIMIT,
      chatMessagesDailyLimit: EXPIRED_LIMIT,
      chatMessagesDailyUsage: EXPIRED_LIMIT,
      extensionCallsLimit: EXPIRED_LIMIT,
      extensionCallsUsage: EXPIRED_LIMIT,
      creditsLimit: EXPIRED_LIMIT,
      creditsUsage: EXPIRED_LIMIT,
    }
  }

  return {
    filesLimit: userLimits?.resourceUsage.files?.limit ?? 0,
    questionnairesLimit: userLimits?.resourceUsage.questionnaires?.limit ?? 0,
    filesUsage: userLimits?.resourceUsage.files?.usage ?? 0,
    questionnairesUsage: userLimits?.resourceUsage.questionnaires?.usage ?? 0,
    chatMessagesLimit: userLimits?.resourceUsage.chatMessages?.limit ?? 0,
    chatMessagesUsage: userLimits?.resourceUsage.chatMessages?.usage ?? 0,
    chatMessagesDailyLimit:
      userLimits?.resourceUsage.chatMessagesDaily?.limit ?? 0,
    chatMessagesDailyUsage:
      userLimits?.resourceUsage.chatMessagesDaily?.usage ?? 0,
    extensionCallsLimit: userLimits?.resourceUsage.extensionCalls?.limit ?? 0,
    extensionCallsUsage: userLimits?.resourceUsage.extensionCalls?.usage ?? 0,
    creditsLimit: userLimits?.resourceUsage.aiCredits?.limit ?? 0,
    creditsUsage: userLimits?.resourceUsage.aiCredits?.usage ?? 0,
  }
}

export default useUserLimits
